import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import {
  Image, Lock, Sparkles, Crown, TrendingUp, Award,
  Check, Eye, SlidersHorizontal, Music2, MapPin, Heart,
} from 'lucide-react';

/* ─── Background Renderer ────────────────────────────────── */
export function ProfileBackground({ userId, children }) {
  const [bg, setBg] = useState(null);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/profile-backgrounds/user/${userId}`)
      .then(r => r.json())
      .then(setBg)
      .catch(() => {});
  }, [userId]);

  if (!bg || !bg.enabled || bg.template_id === 'none') {
    return <>{children}</>;
  }

  const t = bg.template;
  const opacity = (bg.opacity || 8) / 100;
  const blur = bg.blur || 0;
  const brightness = bg.brightness || 100;

  return (
    <div className="relative">
      {/* Background Layer */}
      {bg.custom_image_url ? (
        <div
          className="absolute inset-0 bg-cover bg-center pointer-events-none"
          style={{
            backgroundImage: `url(${bg.custom_image_url})`,
            opacity,
            filter: `blur(${blur}px) brightness(${brightness / 100})`,
          }}
        />
      ) : t?.css ? (
        <div
          className={`absolute inset-0 pointer-events-none ${t.animation ? 'animate-bg-shift' : ''}`}
          style={{
            background: t.css,
            backgroundSize: t.css_size || 'auto',
            opacity: Math.min(opacity * 3, 0.3),
            filter: blur > 0 ? `blur(${blur}px)` : undefined,
          }}
        />
      ) : null}

      {/* Overlay gradient for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/30 to-background/60 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}


/* ─── Category Icons ─────────────────────────────────────── */
const CATEGORY_ICONS = {
  template: SlidersHorizontal,
  influence: Crown,
  achievement: Award,
};

const CATEGORY_LABELS = {
  template: 'Templates',
  influence: 'Influence Unlocks',
  achievement: 'Achievement Unlocks',
};


/* ─── Background Settings Panel ──────────────────────────── */
export function BackgroundSettingsPanel() {
  const { getAuthHeaders } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({
    template_id: 'none', custom_image_url: null,
    opacity: 8, blur: 0, brightness: 100, enabled: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      fetch(`${API}/profile-backgrounds/templates`, { headers }).then(r => r.json()),
      fetch(`${API}/profile-backgrounds/settings`, { headers }).then(r => r.json()),
    ]).then(([tpl, s]) => {
      setTemplates(tpl.templates || []);
      if (s.user_id) setSettings(s);
    }).catch(() => {});
  }, [getAuthHeaders]);

  const save = async (patch) => {
    const updated = { ...settings, ...patch };
    setSettings(updated);
    setSaving(true);
    try {
      await fetch(`${API}/profile-backgrounds/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updated),
      });
    } catch {}
    setSaving(false);
  };

  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'template';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="background-settings-panel">
      {/* Enable Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Custom Background</Label>
          <p className="text-[10px] text-muted-foreground mt-0.5">Personalize your profile appearance</p>
        </div>
        <Switch
          checked={settings.enabled}
          onCheckedChange={(v) => save({ enabled: v })}
          data-testid="bg-enabled-toggle"
        />
      </div>

      {settings.enabled && (
        <div className="space-y-5 animate-in fade-in slide-in-from-top-1 duration-200">
          {/* ── LIVE PREVIEW ────────────────────────────── */}
          {(() => {
            const activeTemplate = templates.find(t => t.id === settings.template_id);
            const previewOpacity = (settings.opacity || 8) / 100;
            const previewBlur = settings.blur || 0;
            const previewBrightness = settings.brightness || 100;

            return (
              <div data-testid="bg-live-preview">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Live Preview</span>
                </div>
                <div className="relative rounded-xl overflow-hidden border border-border h-44 sm:h-52">
                  {/* Background layer */}
                  {settings.custom_image_url ? (
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-all duration-500"
                      style={{
                        backgroundImage: `url(${settings.custom_image_url})`,
                        opacity: previewOpacity,
                        filter: `blur(${previewBlur}px) brightness(${previewBrightness / 100})`,
                      }}
                    />
                  ) : activeTemplate?.css ? (
                    <div
                      className={`absolute inset-0 transition-all duration-500 ${activeTemplate.animation ? 'animate-bg-shift' : ''}`}
                      style={{
                        background: activeTemplate.css,
                        backgroundSize: activeTemplate.css_size || 'auto',
                        opacity: Math.min(previewOpacity * 3, 0.3),
                        filter: previewBlur > 0 ? `blur(${previewBlur}px)` : undefined,
                      }}
                    />
                  ) : null}

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/30 to-background/60" />

                  {/* Mock profile content */}
                  <div className="relative z-10 flex items-end h-full p-4">
                    <div className="flex items-center gap-3 backdrop-blur-md bg-card/30 border border-border/30 rounded-xl p-3 w-full">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                        <Music2 className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-foreground">Your Name</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary/20 text-secondary font-semibold">Enterprise</span>
                          <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                            <MapPin className="w-2.5 h-2.5" /> DMV
                          </span>
                        </div>
                        <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                          <span><strong className="text-foreground">2.4K</strong> Followers</span>
                          <span><strong className="text-foreground">128</strong> Following</span>
                          <span className="flex items-center gap-0.5"><Heart className="w-2.5 h-2.5 text-red-400" /> 890</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Template name overlay */}
                  <div className="absolute top-2 right-2 bg-background/70 backdrop-blur-sm text-[9px] text-muted-foreground px-2 py-0.5 rounded-full border border-border/30">
                    {activeTemplate?.name || 'Default'}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Template Picker */}
          {Object.entries(grouped).map(([category, items]) => {
            const CatIcon = CATEGORY_ICONS[category] || Sparkles;
            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-2">
                  <CatIcon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {CATEGORY_LABELS[category] || category}
                  </span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {items.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => !t.locked && save({ template_id: t.id })}
                      disabled={t.locked}
                      className={`relative rounded-lg border p-3 text-center transition-all text-[11px] ${
                        settings.template_id === t.id
                          ? 'border-secondary bg-secondary/10 ring-1 ring-secondary/30'
                          : t.locked
                            ? 'border-border/50 opacity-50 cursor-not-allowed'
                            : 'border-border hover:border-muted-foreground/30 hover:bg-muted/20 cursor-pointer'
                      }`}
                      data-testid={`bg-template-${t.id}`}
                    >
                      {/* Preview swatch */}
                      <div
                        className="w-full h-8 rounded-md mb-1.5 border border-border/30"
                        style={{
                          background: t.css || 'hsl(var(--muted) / 0.3)',
                          backgroundSize: t.css_size || 'auto',
                        }}
                      />
                      <span className="text-foreground font-medium block truncate">{t.name}</span>
                      {t.locked && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/60">
                          <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      )}
                      {settings.template_id === t.id && (
                        <div className="absolute top-1 right-1 w-4 h-4 bg-secondary rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                      {t.unlock_reason && t.locked && (
                        <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{t.unlock_reason}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Controls */}
          <div className="space-y-4 border-t border-border pt-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Background Controls
            </h4>
            
            {/* Opacity */}
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <Label className="text-xs">Opacity</Label>
                <span className="text-xs text-muted-foreground tabular-nums">{settings.opacity}%</span>
              </div>
              <Slider
                value={[settings.opacity]}
                onValueChange={([v]) => save({ opacity: v })}
                min={0} max={30} step={1}
                data-testid="bg-opacity-slider"
              />
            </div>

            {/* Blur */}
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <Label className="text-xs">Blur</Label>
                <span className="text-xs text-muted-foreground tabular-nums">{settings.blur}px</span>
              </div>
              <Slider
                value={[settings.blur]}
                onValueChange={([v]) => save({ blur: v })}
                min={0} max={20} step={1}
                data-testid="bg-blur-slider"
              />
            </div>

            {/* Brightness */}
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <Label className="text-xs">Brightness</Label>
                <span className="text-xs text-muted-foreground tabular-nums">{settings.brightness}%</span>
              </div>
              <Slider
                value={[settings.brightness]}
                onValueChange={([v]) => save({ brightness: v })}
                min={50} max={150} step={5}
                data-testid="bg-brightness-slider"
              />
            </div>
          </div>

          {/* Info footer */}
          <p className="text-[10px] text-muted-foreground leading-tight">
            Background will never overpower text. Overlay gradient is applied automatically for readability.
          </p>
        </div>
      )}
    </div>
  );
}
